const toTrimmedString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim();
};

const normalizeFileType = (value) => {
  const raw = toTrimmedString(value);
  if (!raw) {
    return 'jpeg';
  }

  if (raw.includes('/')) {
    return raw.split('/').pop() || 'jpeg';
  }

  return raw.replace(/^\./, '') || 'jpeg';
};

const normalizeImageNode = (node, fallbackImageType = 'original') => {
  if (!node || typeof node !== 'object') {
    return null;
  }

  const imageUrl = toTrimmedString(node.image_url || node.img_url || node.imageUrl || node.url);
  const hashId = toTrimmedString(node.hash_id || node.hashId || node.id);
  const fileType = normalizeFileType(node.file_type || node.fileType || node.mime_type || node.mimeType);
  const imageType = toTrimmedString(node.image_type || node.imageType || fallbackImageType) || fallbackImageType;

  if (!imageUrl && !hashId) {
    return null;
  }

  return {
    hashId: hashId || null,
    imageUrl: imageUrl || null,
    fileType,
    imageType
  };
};

const resolveRelationPayload = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if (payload.original_image || payload.processed_image || payload.hash_id || payload.relation_id) {
    return payload;
  }

  if (payload.detail && typeof payload.detail === 'object') {
    return resolveRelationPayload(payload.detail);
  }

  if (payload.data && typeof payload.data === 'object') {
    return resolveRelationPayload(payload.data);
  }

  return payload;
};

export function adaptSaveRelationPayloadForApi(payload = {}) {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const hashId = toTrimmedString(
    payload.hash_id ||
    payload.hashId ||
    payload.originalHashId
  );
  const imageUrl = toTrimmedString(
    payload.img_url ||
    payload.image_url ||
    payload.imageUrl ||
    payload.original_image?.image_url
  );
  const imageType = toTrimmedString(payload.image_type || payload.imageType || 'original') || 'original';

  if (!hashId || !imageUrl) {
    return payload;
  }

  const normalizedPayload = {
    hash_id: hashId,
    img_url: imageUrl,
    file_type: normalizeFileType(payload.file_type || payload.fileType),
    image_type: imageType
  };

  if (imageType === 'processed') {
    const originalHashId = toTrimmedString(payload.original_hash_id || payload.originalHashId);
    if (originalHashId) {
      normalizedPayload.original_hash_id = originalHashId;
    }
  }

  return normalizedPayload;
}

export function normalizeRelationResponse(response = {}) {
  if (!response || typeof response !== 'object') {
    return {
      status: 500,
      payload: null,
      detail: null
    };
  }

  const status = Number(response.status) || 500;
  const payload = response.payload ?? null;
  const detail = response.detail ?? payload?.detail ?? null;

  return {
    status,
    payload,
    detail
  };
}

export function normalizeRelationPayloadForStore(payload = {}) {
  const relation = resolveRelationPayload(payload);
  if (!relation || typeof relation !== 'object') {
    return null;
  }

  const original = normalizeImageNode(
    relation.original_image || relation.originalImage || relation.original,
    'original'
  );
  const processed = normalizeImageNode(
    relation.processed_image || relation.processedImage || relation.processed,
    'processed'
  );
  const hashId = toTrimmedString(
    relation.hash_id ||
    relation.hashId ||
    original?.hashId ||
    processed?.hashId
  );

  if (!hashId && !original && !processed) {
    return null;
  }

  return {
    hashId: hashId || null,
    relationId: toTrimmedString(relation.user_image_relation_id || relation.relation_id || relation.relationId) || null,
    updatedAt: toTrimmedString(relation.updated_at || relation.updatedAt) || null,
    original,
    processed,
    raw: relation
  };
}

export function normalizeHistoryResponse(response = {}) {
  if (!response || typeof response !== 'object') {
    return {
      success: false,
      data: [],
      error: 'invalid_response',
      dataSource: 'real',
      scenarioId: null,
      meta: null
    };
  }

  return {
    success: Boolean(response.success),
    data: Array.isArray(response.data) ? response.data : [],
    error: response.error || null,
    dataSource: response.dataSource || 'real',
    scenarioId: response.scenarioId || null,
    meta: response.meta && typeof response.meta === 'object' ? response.meta : null
  };
}
